import { hydrationManager } from '@/lib/services/reference-hydration.js';
import { useReferenceStore } from '@/store/reference.store.js';
import {
    getEntityStrategy,
    LOAD_STRATEGIES,
    REFERENCE_ENTITIES,
} from '@/config/reference.config.js';
import { createIntegrationLogger } from '@/lib/app-log.js';
import { logReferenceDetailSync } from '@/lib/reference-sync/reference-detail-sync-debug.js';

const invalidationLog = createIntegrationLogger('reference-invalidation');

/**
 * Apply a server-driven `reference_invalidated` event (socket / cross-tab).
 * Cacheable entities are force-refreshed; resolve-only / remote-search entities
 * only bump `versions[entity]` so paginated list hooks can refetch.
 *
 * @param {string} entity — registry key from payload
 * @param {number} [serverVersion] — monotonic version from `referenceVersionTracker`
 */
export function applyReferenceInvalidation(entity, serverVersion) {
    if (!entity) return;

    if (!REFERENCE_ENTITIES.includes(entity)) {
        invalidationLog.debug('ignored unknown reference entity', { entity });
        logReferenceDetailSync('invalidation', 'ignored unknown entity', { entity, serverVersion });
        return;
    }

    const store = useReferenceStore.getState();
    const localVersion = store.versions[entity] ?? 0;

    // Equal → already applied. server < local → Redis/version regression; still refresh.
    if (serverVersion != null && serverVersion === localVersion) {
        invalidationLog.debug('skipped duplicate invalidation', {
            entity,
            serverVersion,
            localVersion,
        });
        logReferenceDetailSync('invalidation', 'SKIPPED duplicate (serverVersion === localVersion)', {
            entity,
            serverVersion,
            localVersion,
        });
        return;
    }

    const strategy = getEntityStrategy(entity);

    if (
        strategy === LOAD_STRATEGIES.RESOLVE_ONLY
        || strategy === LOAD_STRATEGIES.REMOTE_SEARCH
    ) {
        store.bumpEntityVersion(entity, serverVersion);
        logReferenceDetailSync('invalidation', 'bumped version (remoteSearch/resolveOnly)', {
            entity,
            serverVersion,
            localVersion,
            nextVersion: useReferenceStore.getState().versions[entity],
            strategy,
        });
        return;
    }

    logReferenceDetailSync('invalidation', 'hydration refresh (bulk/lazy)', {
        entity,
        serverVersion,
        localVersion,
        strategy,
    });
    hydrationManager.refreshEntity(entity, { force: true });
}
